/**
 * StoryDeviceButton (spec 013 v7): the story editor toolbar's "📡 Device"
 * button, shared by the story studio and Under the hood (D-3). It shows the
 * connection state as the scene editor's `DeviceButton` does and opens a
 * small menu, "Play on a device":
 *
 * - "From “<selected scene>”": the story from the selected scene onward;
 * - "Whole story": the story from its start scene;
 * - "Connection…": the device modal (QR code, Disconnect).
 *
 * A disabled item stays visible with `aria-disabled` and its reason. Choosing
 * a story item starts a new run on the device controller; with no device
 * connected the device modal opens (creating the session) and the story goes
 * out when a phone joins (`useDeviceStorySync`). The menu keeps to the
 * studio's "Words on screen" (spec 025).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDevicePreview } from '../contexts/DevicePreviewContext';
import { useModalContext } from '../contexts/ModalContext';
import DeviceConnectModal from '../modals/DeviceConnectModal';
import type { StoryApiResponse } from '../../../Shared/src/@types';
import styles from './StoryDeviceButton.module.scss';

/** Where "From …" starts, or why it cannot. */
export type StoryDeviceFrom =
  | { ok: true; storySceneId: number; name: string }
  | { ok: false; reason: string };

/** What the menu offers for this story and selection. */
export interface StoryDeviceTargets {
  from: StoryDeviceFrom;
  /** Null when the whole story can play, else why not. */
  wholeDisabledReason: string | null;
}

const sceneName = (story: Pick<StoryApiResponse, 'storyScenes'>, storySceneId: number): string => {
  const ss = story.storyScenes.find((s) => s.id === storySceneId);
  return ss?.sceneName || (ss ? `Scene #${ss.sceneId}` : 'Scene');
};

/**
 * The selected scene for the Device menu (spec 013): in the studio the
 * studio's selected scene; under the hood the active scene tab's scene (at
 * its first scene node), else the scene node selected on the map.
 */
export function storyDeviceTargets(
  story: Pick<StoryApiResponse, 'graph' | 'storyScenes'>,
  view: 'studio' | 'hood',
  selectedNodeId: string | null,
  activeSceneId: number | null,
): StoryDeviceTargets {
  const sceneNodes = story.graph.nodes.filter((n) => n.type === 'scene');
  const start = sceneNodes.find((n) => n.id === story.graph.startNodeId);
  const wholeDisabledReason = start ? null : 'Set a start scene first';

  const fromNode = (nodeId: string | null): StoryDeviceFrom | null => {
    const node = nodeId ? sceneNodes.find((n) => n.id === nodeId) : undefined;
    return node ? { ok: true, storySceneId: node.storySceneId, name: sceneName(story, node.storySceneId) } : null;
  };

  let from: StoryDeviceFrom;
  if (view === 'hood' && activeSceneId !== null) {
    const rows = story.storyScenes.filter((ss) => ss.sceneId === activeSceneId);
    const node = sceneNodes.find((n) => rows.some((ss) => ss.id === n.storySceneId));
    from = node
      ? { ok: true, storySceneId: node.storySceneId, name: sceneName(story, node.storySceneId) }
      : { ok: false, reason: "This scene isn't on the map yet" };
  } else {
    from = fromNode(selectedNodeId)
      ?? { ok: false, reason: view === 'studio' && sceneNodes.length === 0 ? 'Add a scene first' : 'Select a scene first' };
  }
  return { from, wholeDisabledReason };
}

export interface StoryDeviceButtonProps {
  story: StoryApiResponse;
  view: 'studio' | 'hood';
  selectedNodeId: string | null;
  /** Under the hood: the active scene tab's scene id, or null on the map tab. */
  activeSceneId: number | null;
  className?: string;
}

const StoryDeviceButton: React.FC<StoryDeviceButtonProps> = ({ story, view, selectedNodeId, activeSceneId, className }) => {
  const device = useDevicePreview();
  const { addModal, removeModal } = useModalContext();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { status, playerCount, message } = device;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const openModal = useCallback(() => {
    addModal({ title: 'Connect Device', body: <DeviceConnectModal onClose={() => removeModal()} />, size: 'sm' });
  }, [addModal, removeModal]);

  const targets = open ? storyDeviceTargets(story, view, selectedNodeId, activeSceneId) : null;

  const play = (startAt?: { storySceneId: number; name: string }) => {
    setOpen(false);
    device.startStoryRun?.({
      storyId: Number(story.id),
      ...(startAt ? { startAtStorySceneId: startAt.storySceneId, label: startAt.name } : {}),
    });
    // Connected: useDeviceStorySync sends it now. Otherwise the modal connects a phone first.
    if (status !== 'connected') openModal();
  };

  const title =
    status === 'connected' ? `${playerCount} device${playerCount === 1 ? '' : 's'} connected`
      : status === 'creating' ? 'Creating a device session…'
        : status === 'waiting' ? 'Waiting for a device…'
          : status === 'error' ? (message ?? 'Device preview failed')
            : message ?? 'Play the story on a phone';

  const item = (
    testId: string,
    label: string,
    disabledReason: string | null,
    onChoose: () => void,
  ) => (
    <button
      type="button"
      role="menuitem"
      className={`${styles.item} ${disabledReason ? styles.itemDisabled : ''}`}
      data-testid={testId}
      aria-disabled={disabledReason ? 'true' : undefined}
      title={disabledReason ?? undefined}
      onClick={() => {
        if (!disabledReason) onChoose();
      }}
    >
      <span className={styles.itemLabel}>{label}</span>
      {disabledReason && <span className={styles.itemReason}>{disabledReason}</span>}
    </button>
  );

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={`${className ?? ''} ${styles.button} ${status === 'connected' ? styles.connected : ''}`}
        onClick={() => setOpen((v) => !v)}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="story-device-button"
        data-status={status}
      >
        {status === 'connected' && (
          <span className={styles.label}>
            <span className={styles.dot} data-testid="story-device-dot" data-dot="green" />
            📡 {playerCount}
          </span>
        )}
        {(status === 'creating' || status === 'waiting') && (
          <span className={styles.label}>
            <Spinner animation="border" size="sm" /> 📡
          </span>
        )}
        {status === 'error' && (
          <span className={styles.label}>
            <span className={`${styles.dot} ${styles.dotError}`} data-testid="story-device-dot" data-dot="red" />
            📡
          </span>
        )}
        {status === 'idle' && '📡 Device'}
      </button>

      {open && targets && (
        <div className={styles.menu} role="menu" aria-label="Play on a device" data-testid="story-device-menu">
          <div className={styles.heading}>Play on a device</div>
          {item(
            'story-device-from',
            targets.from.ok ? `From “${targets.from.name}”` : 'From the selected scene',
            targets.from.ok ? null : targets.from.reason,
            () => {
              if (targets.from.ok) play(targets.from);
            },
          )}
          {item('story-device-whole', 'Whole story', targets.wholeDisabledReason, () => play())}
          <div className={styles.separator} />
          {item('story-device-connection', 'Connection…', null, () => {
            setOpen(false);
            openModal();
          })}
        </div>
      )}
    </div>
  );
};

export default StoryDeviceButton;
